<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import NamePlate from '@/components/plates/NamePlate.vue'
import { trackEvent, AnalyticalEvents } from '@/utils/analytics'

const props = defineProps({
    platformId: { type: String, required: true },
    platformUserId: { type: String, required: true },
    modal: { type: String, required: true },
    logo: { type: String, default: null },
    name: { type: String, default: null },
    displayName: { type: String, default: null },
})

const store = useStore()
const router = useRouter()
const route = useRoute()

const unlockState = computed(
    () => store.getters.unlocks[`profile-unlock:superview:${props.platformId}:${props.platformUserId}`]
)

const isLegacyBilling = computed(() => store.getters.isLegacyBilling)

const profileRoute = computed(() => ({
    path: route.path,
    query: {
        ...route.query,
        modal: props.modal,
        platform_id: props.platformId,
        platform_user_id: props.platformUserId,
    },
}))

const profileHref = computed(() => router.resolve(profileRoute.value).href)

const initialize = async () => {
    if (isLegacyBilling.value !== true) {
        store.dispatch('fetchRevealStatus', {
            quotaType: 'profile-unlock',
            keyIds: ['superview', props.platformId, props.platformUserId],
        })
    }
}
initialize()

const handleClick = async () => {
    // Track that an unlock was clicked for a creator
    trackEvent(AnalyticalEvents.ProfileUnlocked, {
        creator_count: 1,
        origin: 'Table data',
    })

    // Profile unlock handled in the creator modal
    router.push(profileRoute.value)
}

const statusIcon = computed(() => {
    if (isLegacyBilling.value) {
        return null
    }
    if (unlockState?.value?.loading || unlockState.value === undefined) {
        return 'spinner-third'
    }
    if (unlockState.value?.is_found && unlockState.value.unlock?.id) {
        return null
    }
    return 'lock'
})

const statusIconClass = computed(() => {
    if (!statusIcon.value) {
        return ''
    }
    if (unlockState?.value?.loading || unlockState.value === undefined) {
        return 'animate-spin text-slate-500'
    }
    return 'text-slate-500'
})

const handleMiddleClick = () => {
    window.open(profileHref.value, '_blank')
}

</script>

<template>
    <div class="relative flex w-full max-w-full items-center">
        <a
            :href="profileHref"
            class="flex min-w-0 max-w-full cursor-pointer items-center gap-2"
            @click.prevent="handleClick"
            @mousedown.middle.prevent="handleMiddleClick"
        >
            <NamePlate
                :logo="logo"
                :name="name"
                :display-name="displayName"
                :platform-id="platformId"
                :platform-user-id="platformUserId"
                :is-drawer-enabled="false"
                :is-interactive="true"
            />

            <template v-if="statusIcon">
                <AppIcon
                    :name="statusIcon"
                    :class="['h-4 w-4', statusIconClass]"
                />
            </template>
        </a>
    </div>
</template>
